import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { createSelector } from 'reselect';
import { Link } from "react-router-dom";
import { Key, User } from "lucide-react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from 'react-toastify';

import { auth, storage, db } from "../../config/firebase/firebaseConfig";
import { updateProfile, reload } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, updateDoc, getDoc } from "firebase/firestore";

const Profile = () => {
  document.title = "Profile | LyfeAdmin";

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    photoURL: ''
  });

  const selectProperties = createSelector(
    (state: any) => state.Profile,
    (profile) => ({
      user: profile.user,
      error: profile.error,
      success: profile.success
    })
  );

  const { user } = useSelector(selectProperties);

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      setUserInfo({
        name: currentUser.displayName || 'Admin',
        email: currentUser.email || '',
        photoURL: currentUser.photoURL || ''
      });
    }
  }, [user]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: userInfo.name,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please Enter Your Name"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          let photoURL = userInfo.photoURL;

          if (imageFile) {
            const imageRef = ref(storage, `profile_images/${currentUser.uid}`);
            const snapshot = await uploadBytes(imageRef, imageFile);
            photoURL = await getDownloadURL(snapshot.ref);
          }

          await updateProfile(currentUser, {
            displayName: values.name,
            photoURL: photoURL
          });

          // Force reload the user to trigger auth state change listeners
          await reload(currentUser);

          try {
            const userDocRef = doc(db, 'admins', currentUser.uid);
            const userDoc = await getDoc(userDocRef);
            
            if (userDoc.exists()) {
              await updateDoc(userDocRef, {
                name: values.name,
                photoUrl: photoURL,
                updatedAt: new Date()
              });
            }
          } catch (firestoreError) {
            console.log("Firestore update not needed or failed:", firestoreError);
          }

          setUserInfo({
            name: values.name,
            email: userInfo.email, // Keep existing email
            photoURL: photoURL
          });

          toast.success("Profile updated successfully!");
        }
      } catch (error: any) {
        console.error("Profile update error:", error);
        toast.error("Failed to update profile: " + error.message);
      } finally {
        setLoading(false);
      }
    }
  });

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const getProfileImage = () => {
    if (selectedImage) return selectedImage;
    if (userInfo.photoURL) return userInfo.photoURL;
    return null; // Return null to show icon instead
  };

  const hasProfileImage = () => {
    return selectedImage || userInfo.photoURL;
  };

  return (
    <React.Fragment>
      <div className="container-fluid group-data-[content=boxed]:max-w-boxed mx-auto">
        <div className="flex items-center justify-between mb-6 mt-8">
          <h4 className="text-lg font-medium text-slate-700 dark:text-zink-100">Profile</h4>
          <div className="flex items-center gap-2">
            <Link
              to="/password-settings"
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-custom-500 bg-white border border-custom-500 rounded-md hover:bg-custom-50 hover:border-custom-600 focus:bg-custom-50 focus:border-custom-600 focus:ring focus:ring-custom-100 active:bg-custom-50 active:border-custom-600 active:ring active:ring-custom-100 dark:bg-zink-700 dark:border-custom-500 dark:text-custom-500 dark:hover:bg-custom-500/10 dark:focus:bg-custom-500/10 dark:active:bg-custom-500/10"
            >
              <Key className="inline-block size-4 mr-1" />
              Password Setting
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12">
          <div className="xl:col-span-3">
            <div className="card">
              <div className="card-body p-0">
                <div className="flex items-center p-4 bg-custom-500 text-white">
                  <div className="shrink-0">
                    {hasProfileImage() ? (
                      <img
                        src={getProfileImage()}
                        alt="Profile"
                        className="size-16 rounded-full border-2 border-white object-cover"
                      />
                    ) : (
                      <div className="size-16 rounded-full border-2 border-white bg-white/20 flex items-center justify-center">
                        <User className="size-8 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="ml-3">
                    <h4 className="text-white font-medium">{userInfo.name || 'Admin'}</h4>
                  </div>
                </div>
                <div className="p-0">
                  <div className="flex items-center justify-between p-3 border-b border-slate-200 dark:border-zink-500">
                    <span className="text-slate-500 dark:text-zink-300">Name</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{userInfo.name || 'Admin'}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 border-b border-slate-200 dark:border-zink-500">
                    <span className="text-slate-500 dark:text-zink-300">Username</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">admin</span>
                  </div>
                  <div className="flex items-center justify-between p-3">
                    <span className="text-slate-500 dark:text-zink-300">Email</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{userInfo.email}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="xl:col-span-9">
            <div className="card">
              <div className="card-body">
                <h5 className="mb-4 text-16 font-medium border-b border-slate-200 dark:border-zink-500 pb-2">
                  Profile Information
                </h5>

                <form onSubmit={validation.handleSubmit}>
                  <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
                    <div className="lg:col-span-4">
                      <label className="inline-block mb-2 text-base font-medium">Image</label>
                      <div className="relative">
                        <div className="size-32 mx-auto mb-4 rounded-full shadow-md bg-slate-100 profile-user dark:bg-zink-500 overflow-hidden flex items-center justify-center">
                          {hasProfileImage() ? (
                            <img
                              src={getProfileImage()}
                              alt="Profile"
                              className="object-cover w-full h-full rounded-full"
                            />
                          ) : (
                            <User className="size-16 text-slate-400 dark:text-zink-300" />
                          )}
                        </div>
                        <div className="absolute bottom-0 right-1/2 transform translate-x-1/2 translate-y-2">
                          <label
                            htmlFor="profile-image-input"
                            className="flex items-center justify-center size-8 bg-custom-500 text-white rounded-full cursor-pointer hover:bg-custom-600 transition-colors"
                          >
                            <i className="ri-camera-line text-sm"></i>
                          </label>
                          <input
                            id="profile-image-input"
                            type="file"
                            className="hidden"
                            accept=".png,.jpg,.jpeg"
                            onChange={handleImageChange}
                          />
                        </div>
                      </div>
                      <div className="mt-2">
                        <small className="text-slate-500 dark:text-zink-300">
                          Supported Files: <b>.png, .jpg, .jpeg.</b>
                          <br />
                          Image will be resized into <b>400x400</b>px
                        </small>
                      </div>
                    </div>

                    <div className="lg:col-span-8">
                      <div className="grid grid-cols-1 gap-5">
                        <div>
                          <label htmlFor="name" className="inline-block mb-2 text-base font-medium">
                            Name
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                            placeholder="Enter your name"
                            value={validation.values.name}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                          />
                          {validation.touched.name && validation.errors.name && (
                            <div className="mt-1 text-sm text-red-500">{validation.errors.name}</div>
                          )}
                        </div>

                        <div>
                          <label htmlFor="email" className="inline-block mb-2 text-base font-medium">
                            Email
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            disabled
                            className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                            placeholder="Enter your email"
                            value={userInfo.email}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end mt-6">
                    <button
                      type="submit"
                      disabled={loading}
                      className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <i className="ri-loader-2-line animate-spin mr-2"></i>
                          Updating...
                        </>
                      ) : (
                        'Submit'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Profile;